import React from 'react';
import { Button, FormHelperText, TextField } from '@mui/material';
import { AppContext } from '../../../App';
import { IFieldReview } from './FieldList';
import { IToken } from '../../../types/my-types';
import { MarkDispatchContext, MarkStateContext } from '../../../context/MarkContext';
import { useCreateCorrection } from '../../hooks/useCreateCorrection';
import { useQueryClient } from '@tanstack/react-query';
import { validateLabel } from '../../../utils/validationUtils';

interface CorrectionNewProps {
  ebId: string;
  extractionId: string;
  fields: IFieldReview[];
}

const CorrectionNew: React.FC<CorrectionNewProps> = ({ ebId, extractionId, fields }) => {
  const { setToast } = React.useContext(AppContext);
  const { activeField, tokens } = React.useContext(MarkStateContext);

  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);

  // TODO: use reducer for this
  const [fieldname, setFieldname] = React.useState('');
  const [labelError, setLabelError] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [content, setContent] = React.useState('');
  const cache = useQueryClient();

  const mutationCreateCorrection = useCreateCorrection(false, true, {
    onSuccess: () => {
      cache.invalidateQueries({
        queryKey: ['extraction']
      });
      setFieldname('');
      setContent('');
      dispatch({ type: 'RESET_TOKENS' });
      setToast({
        variant: 'success',
        open: true,
        message: 'Correction successfully added.',
      });
    },
    onError: () => {
      cache.invalidateQueries({
        queryKey: ['extraction', ebId]
      });
      setToast({
        variant: 'error',
        open: true,
        message: 'Sorry, failed to save correction',
      });
    },
  });

  React.useEffect(() => {
    if (tokens.length !== 0 && !activeField) {
      // state.tokens are immutable, so deep copy for sort
      let mytokens: IToken[] = [...tokens];
      setContent(
        mytokens
          .sort((a: IToken, b: IToken) => a.bbox[0].x - b.bbox[0].x)
          .map((t: IToken) => t.content)
          .join(' ')
      );
    }
  }, [tokens, activeField]);

  const handleSave = () => {
    const newCorrection = { content, tokens };
    mutationCreateCorrection.mutate({
      extraction: extractionId,
      field: fieldname,
      correction: newCorrection,
    });
    /*
			try {
				await createCorrection({
					variables: {
						dataPoolId,
						input: {
							extraction: extractionId,
							field: fieldname,
							correction: newCorrection,
						},
					},
					update: (store, { data }) => {
						const correctionsCache: any = store.readQuery({
							query: GET_ALL_CORRECTIONS,
						});
						store.writeQuery({
							query: GET_ALL_CORRECTIONS,
							data: {
								corrections: {
									...correctionsCache.corrections,
									results: [
										...correctionsCache.corrections.results,
										data.createCorrection,
									],
								},
							},
						});
					},
					// TODO: why refetch extraction batch?
					refetchQueries: [
						{
							query: GET_EXTRACTION_BATCH,
							variables: { dataPoolId, id: ebId },
						},
						{
							query: GET_EXTRACTION,
							variables: { dataPoolId, id: extractionId },
						},
					],
				});
				setFieldname("");
				setContent("");
				dispatch({ type: "RESET_TOKENS" });
			} catch (error) {
				const errorCode = (error.networkError as any).statusCode;
				if (errorCode === 503) {
					setMaintenance(true);
				} else {
					setToast({
						variant: "error",
						open: true,
						message: "Sorry, failed to save correction",
					});
				}
			}
				*/
  };

  // TODO: refactor new field?
  return (
    <form id="new-correction-form" style={{ paddingTop: '8px' }}>
      <TextField
        autoComplete="off"
        data-cy="new-correction-form-fieldname"
        fullWidth
        id="fieldname"
        // InputLabelProps={{ shrink: true }}
        label="Field Name"
        margin="dense"
        onChange={(e) => {
          const label = e.target.value;
          const validationResult = validateLabel(
            label,
            fields.map((f) => f.label)
          );

          if (!validationResult.isValid) {
            setLabelError(true);
            setErrorMessage(validationResult.errorMessage);
          } else {
            setLabelError(false);
            setErrorMessage('');
          }

          setFieldname(label);
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' && !labelError && fieldname.trim() !== '') {
            e.preventDefault();
            handleSave();
          }
        }}
        name="fieldname"
        style={{ backgroundColor: 'white' }}
        value={fieldname}
        variant="outlined"
      />
      {labelError && errorMessage.length > 0 && (
        <FormHelperText id="label-field-error" data-cy="label-field-error" style={{ color: 'red' }}>
          {errorMessage}
        </FormHelperText>
      )}
      <TextField
        // InputLabelProps={{ shrink: true }}
        autoComplete="off"
        fullWidth
        id="content"
        label="Content"
        margin="dense"
        name="content"
        onChange={(e) => {
          setContent(e.target.value);
        }}
        onFocus={() => {
          dispatch({ type: 'ADDING_NEW_FIELD' });
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' && !labelError && content.trim() !== '') {
            e.preventDefault();
            handleSave();
          }
        }}
        style={{ backgroundColor: 'white' }}
        value={content}
        variant="outlined"
      />
      <Button
        id="add"
        data-cy="button-add-fieldName"
        color="primary"
        variant="contained"
        disabled={labelError}
        onClick={handleSave}
        style={{ marginTop: '6px' }}
      >
        Add
      </Button>
      <Button
        id="reset"
        data-cy="button-reset-fieldName"
        onClick={() => {
          setFieldname('');
          setContent('');
          dispatch({ type: 'SAVE_FIELD' });
        }}
      >
        Reset
      </Button>
    </form>
  );
};

export default CorrectionNew;
